import { useMemo } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { Trans } from "react-i18next"

import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useService } from "hooks/service/useService"
import { useAppUI } from "providers/ui"
import { useVenue } from "providers/venue"

import { Head } from "components/Head"
import { EmptyData } from "components/EmptyData"
import { MotionDiv, showContent } from "components/motions"

import { ServiceIcon } from "components/icons"
import { ServiceCard } from "components/domains/service/ServiceCard"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { VenueSelector } from "components/VenueSelector"

export const ServicesKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const { openSearch } = useAppUI()
  const {
    evenColServices,
    oddColServices,
    hasData,
    locationVenueId,
    venueFilter,
    changeVenueFilter,
  } = useService()
  const theme = useTheme()

  const { venues, isMultiVenueProject } = useVenue()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.service) || <ServiceIcon />,
    [theme]
  )

  const { styleOverrides = {} } = theme.components?.[`ServicePage`] || {}
  const {
    root,
    gridContainer,
    gridItem,
    emptyDataContainer,
    navigationWrapper,
    titleTextContainer,
    titleText,
    highlightedText,
    toolbarContainer,
    actionContainer,
    languageSelectMenu,
    actionButton,
    searchBarContainer,
    venueSelector,
  } = styleOverrides

  const highlightedTextComp = (
    //@ts-ignore
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  return (
    <>
      <Head title={t("Service & Privilege")} />
      <Layout>
        <Box sx={root}>
          <Box sx={titleTextContainer}>
            <Typography variant="h2" sx={titleText}>
              <Trans
                components={{
                  highlight: highlightedTextComp,
                }}
              >
                {t("Service & Privilege Title Page")}
              </Trans>
            </Typography>
          </Box>
          {hasData ? (
            <MotionDiv
              variants={showContent}
              initial="initial"
              animate="animate"
              sx={gridContainer}
            >
              <Box sx={gridItem}>
                {evenColServices.map((service, index) => (
                  <ServiceCard
                    key={`service-even-item-${index}`}
                    data={service}
                  />
                ))}
              </Box>
              <Box sx={gridItem}>
                {oddColServices.map((service, index) => {
                  return (
                    <ServiceCard
                      key={`service-odd-item-${index}`}
                      data={service}
                    />
                  )
                })}
              </Box>
            </MotionDiv>
          ) : (
            <MotionDiv
              variants={showContent}
              initial="initial"
              animate="animate"
              sx={emptyDataContainer}
            >
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Check Back Soon")}
              />
            </MotionDiv>
          )}
          <Box key="app-navigation" sx={navigationWrapper}>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageSelectMenu sx={languageSelectMenu} />
                <LanguageButton variant="contained" sx={actionButton} />
                {/* //TODO:: Add a wheel chair's mode handler function later */}
                {/*  <Button
                        variant="contained"
                        sx={actionButton}
                        onClick={() => {}}
                        startIcon={<RestroomWheelchairIcon />}
                      /> */}
                {additionalMenu}
                {/* //TODO:: Create Toolbar component and refactor Code */}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
